import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type {
  HistoricalBar,
  MarketQuote,
  MarketQuotesData,
  MarketStatusData,
} from './types/market.ts';

export interface ContractQuoteRequest {
  symbol: string;
  sec_type?: string;
  con_id?: number;
  exchange?: string;
  currency?: string;
  last_trade_date_or_contract_month?: string;
  local_symbol?: string;
  trading_class?: string;
  multiplier?: string;
  prefer_live?: boolean;
}

/** Live quote, market status, and chart-history API endpoints. */
export class MarketApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getHistoricalData(
    symbol: string,
    timeframe: string,
    duration: string,
    source: 'auto' | 'ibkr' = 'auto',
    signal?: AbortSignal,
  ): Promise<APIResponse & { data?: HistoricalBar[] }> {
    const params = new URLSearchParams({ timeframe, duration, source });
    const endpoint = `${this.baseUrl}/market/history/${encodeURIComponent(symbol)}?${params.toString()}`;
    return this.request<APIResponse & { data?: HistoricalBar[] }>(endpoint, {
      timeoutMs: 20000,
      retries: 1,
      signal,
    });
  }

  getMarketStatus(): Promise<APIResponse<MarketStatusData>> {
    return this.request(`${this.baseUrl}/market/status`, { timeoutMs: 8000, retries: 1 });
  }

  getQuotes(symbols: string[]): Promise<APIResponse<MarketQuotesData>> {
    const endpoint = `${this.baseUrl}/market/quotes?symbols=${encodeURIComponent(symbols.join(','))}`;
    return this.request<APIResponse<MarketQuotesData>>(endpoint);
  }

  getContractQuote(contract: ContractQuoteRequest): Promise<APIResponse<MarketQuote | null>> {
    return this.request<APIResponse<MarketQuote | null>>(`${this.baseUrl}/market/quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contract),
    });
  }
}
